/**
 * JIM-1027-24 (the answer to JIM-1023-07, "when we go back to prior screen I
 * have to start all over instead of where I was"): coming back to the Sites
 * list from a site restores the search, the page and the scroll position.
 *
 * Kept per browser tab (sessionStorage) and for a bounded time, so a fresh
 * visit later in the day starts clean. A blank, first-page, top-of-list state
 * is "nothing to remember" and clears the entry.
 */
export type SitesMemory = {
  search: string;
  page: number;
  scrollY: number;
  at: number;
};

export const SITES_MEMORY_KEY = 'lite-sites-memory';
export const SITES_MEMORY_TTL_MS = 30 * 60 * 1000;

type Store = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

function defaultStore(): Store | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function readSitesMemory(
  now: number = Date.now(),
  store: Store | null = defaultStore()
): SitesMemory | null {
  if (!store) return null;
  try {
    const raw = store.getItem(SITES_MEMORY_KEY);
    if (!raw) return null;
    const m = JSON.parse(raw) as Partial<SitesMemory>;
    if (typeof m.at !== 'number' || now - m.at > SITES_MEMORY_TTL_MS) {
      return null;
    }
    const page =
      typeof m.page === 'number' && Number.isInteger(m.page) && m.page >= 1
        ? m.page
        : 1;
    return {
      search: typeof m.search === 'string' ? m.search : '',
      page,
      scrollY: typeof m.scrollY === 'number' && m.scrollY > 0 ? m.scrollY : 0,
      at: m.at,
    };
  } catch {
    return null;
  }
}

export function writeSitesMemory(
  memory: Omit<SitesMemory, 'at'>,
  now: number = Date.now(),
  store: Store | null = defaultStore()
): void {
  if (!store) return;
  try {
    const blank =
      !memory.search.trim() && memory.page <= 1 && memory.scrollY <= 0;
    if (blank) {
      store.removeItem(SITES_MEMORY_KEY);
      return;
    }
    store.setItem(SITES_MEMORY_KEY, JSON.stringify({ ...memory, at: now }));
  } catch {
    // Storage blocked or full: the list starts clean next time.
  }
}
